import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  FileText,
  Download,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  Plus,
  File
} from 'lucide-react';

const API_BASE = '';

// ============================================================================
// REPORT STATUS BADGE
// ============================================================================

function ReportStatusBadge({ status }) {
  const statusConfig = {
    completed: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Completed' },
    pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
    processing: { icon: Loader, color: 'bg-blue-100 text-blue-800', label: 'Processing' },
    failed: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Failed' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge className={config.color}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

// ============================================================================
// REPORT FORMAT ICON
// ============================================================================

function ReportFormatIcon({ format }) {
  const icons = {
    pdf: <File className="w-4 h-4 text-red-600" />,
    excel: <FileText className="w-4 h-4 text-green-600" />,
    json: <File className="w-4 h-4 text-blue-600" />,
  };

  return icons[format] || <FileText className="w-4 h-4 text-gray-600" />;
}

// ============================================================================
// NEW REPORT DIALOG
// ============================================================================

function NewReportDialog({ open, onOpenChange, onSubmit }) {
  const [formData, setFormData] = useState({
    report_type: 'merchant_processing',
    format: 'pdf',
    parameters: {},
    send_email: false,
    merchant_url: '',
    company_name: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Generate New Report</DialogTitle>
          <DialogDescription>
            Create a new merchant or workflow report in your preferred format
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Report Type</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.report_type}
              onChange={(e) => setFormData({ ...formData, report_type: e.target.value })}
            >
              <option value="merchant_processing">Merchant Processing</option>
              <option value="workflow_status">Workflow Status</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Company Name (Optional)</label>
            <Input
              placeholder="Search by company name..."
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Merchant URL (Optional)</label>
            <Input
              placeholder="https://example.com or search by URL..."
              value={formData.merchant_url}
              onChange={(e) => setFormData({ ...formData, merchant_url: e.target.value })}
              className="w-full"
            />
            <p className="text-xs text-gray-500">Leave blank for all merchants or enter specific merchant URL/company name</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Format</label>
            <select
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={formData.format}
              onChange={(e) => setFormData({ ...formData, format: e.target.value })}
            >
              <option value="pdf">PDF Document</option>
              <option value="excel">Excel Spreadsheet</option>
              <option value="json">JSON Data (Full Merchant Data)</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.send_email}
                onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Send email notification when ready</span>
            </label>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN REPORTS MANAGEMENT COMPONENT
// ============================================================================

export default function ReportsManagement() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [newReportDialogOpen, setNewReportDialogOpen] = useState(false);
  const [downloadingReports, setDownloadingReports] = useState(new Set());
  const itemsPerPage = 15;

  const tenantId = null;  // Changed from UUID to null (tenant is nullable)

  useEffect(() => {
    fetchReports();
    // Auto-refresh every 10 seconds to check for report completion
    const interval = setInterval(fetchReports, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // Build query params - only include tenant_id if it's not null
      const params = new URLSearchParams({ page_size: '200' });
      if (tenantId !== null) {
        params.append('tenant_id', tenantId);
      }
      const response = await fetch(`${API_BASE}/api/v1/enterprise/reports/?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }
      const data = await response.json();
      setReports(data.results || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async (formData) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/enterprise/reports/generate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, tenant_id: tenantId }),
      });

      const result = await response.json();
      if (result.report_id) {
        await fetchReports();
      }
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleDownloadReport = async (reportId, format) => {
    try {
      setDownloadingReports(new Set([...downloadingReports, reportId]));

      const response = await fetch(`${API_BASE}/api/v1/enterprise/reports/${reportId}/download/`);
      
      if (!response.ok) {
        throw new Error('Download failed');
      }

      // Get filename from Content-Disposition header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `report_${reportId.substring(0, 8)}.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setDownloadingReports((prev) => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Download failed. Please try again.');
      setDownloadingReports((prev) => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  const handleCheckStatus = async (reportId) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/enterprise/reports/${reportId}/status/`);
      const data = await response.json();
      
      // Update report in list
      setReports((prevReports) =>
        prevReports.map((report) =>
          report.id === reportId ? { ...report, status: data.status, ...data } : report
        )
      );
    } catch (error) {
      console.error('Error checking report status:', error);
    }
  };

  // Filter and paginate reports
  const filteredReports = reports.filter((report) =>
    report.report_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    report.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="max-w-[1500px] mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports Management</h1>
          <p className="text-gray-600 mt-1">Generate and download merchant & workflow reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReports}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setNewReportDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Report
          </Button>
        </div>
      </div>

      {/* Search Bar */}
      <Card className="p-4">
        <div className="flex items-center space-x-2">
          <Search className="w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by report type or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
          />
        </div>
      </Card>

      {/* Reports Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Report ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Size</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : paginatedReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No reports found. Generate your first report to get started.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell className="font-mono text-xs">
                      {report.id?.substring(0, 8)}...
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-gray-600" />
                        {report.report_type?.replace('_', ' ').toUpperCase()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <ReportFormatIcon format={report.format} />
                        <span className="ml-2">{report.format?.toUpperCase()}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ReportStatusBadge status={report.status} />
                    </TableCell>
                    <TableCell>
                      {new Date(report.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {report.metadata?.file_size
                        ? `${(report.metadata.file_size / 1024).toFixed(1)} KB`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        {report.status === 'completed' ? (
                          <Button
                            size="sm"
                            onClick={() => handleDownloadReport(report.id, report.format)}
                            disabled={downloadingReports.has(report.id)}
                          >
                            {downloadingReports.has(report.id) ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </>
                            )}
                          </Button>
                        ) : report.status === 'pending' || report.status === 'processing' ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCheckStatus(report.id)}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Check Status
                          </Button>
                        ) : (
                          <Badge variant="destructive">Failed</Badge>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {filteredReports.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredReports.length)} of{' '}
              {filteredReports.length} reports
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-4 py-2 text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* New Report Dialog */}
      <NewReportDialog
        open={newReportDialogOpen}
        onOpenChange={setNewReportDialogOpen}
        onSubmit={handleGenerateReport}
      />
    </div>
  );
}
